import type { FC } from 'react';
import { useState } from 'react';
import type { OptionalExchangeRatesCurrentRequest } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type { AuctionShortTerm } from 'src/apis/monolith/types';
import { useAuction } from 'src/apis/monolith/use-auction';
import { useAuctionConfig } from 'src/apis/monolith/use-auction-config';
import { useCompetitions } from 'src/apis/monolith/use-competition-list';
import { useOrders } from 'src/apis/monolith/use-orders';
import { ExpandableCard } from 'src/components/data-display/expandable-card';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import {
  canShowBiddingForm,
  EditBidWarning,
} from 'src/pages/transport/auctions/details/shared-form-logic';
import { ShortTermBiddingForm } from 'src/pages/transport/auctions/details/short-term-bidding-form';
import { AuctionDetailsFromTable } from 'src/pages/transport/auctions/overview/auction-details-from-table';

type Props = {
  orderId?: string;
  auctionId: string;
  onCancel: () => void;
  onSuccess: () => void;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
};

export const EmbeddedShortTermBiddingForm: FC<Props> = ({
  auctionId,
  orderId,
  onCancel,
  onSuccess,
  exchangeRates,
}) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const auction = useAuction({ auctionId });
  const competingAuctions = useCompetitions({ auctionId });
  const auctionConfig = useAuctionConfig({ auctionId, orderId });
  const orders = useOrders({ auctionId, onlyIfShipper: true });

  const [isExpanded, setExpanded] = useState(!orderId);

  if (
    !auction.response ||
    !competingAuctions.response ||
    !auctionConfig.response ||
    orders?.response === null
  )
    return <PageSpinner />;

  const showBiddingForm = canShowBiddingForm({
    auction: auction.response.data,
    monolithUser,
    unifiedBids: undefined, // only for long term
    orders: orders?.response.data,
    orderId,
  });

  return (
    <div className="grid items-center gap-md">
      <ExpandableCard
        header={
          <Heading mode="title" size="sm">
            Auctions Details
          </Heading>
        }
        data-testid={`expandable-card-${auctionId}`}
        isExpanded={isExpanded}
        setExpanded={setExpanded}
      >
        <AuctionDetailsFromTable
          auction={auction.response.data}
          exchangeRates={exchangeRates}
        />
      </ExpandableCard>

      <EditBidWarning
        auction={auction.response.data}
        unifiedBids={undefined}
        orderId={orderId}
        showBiddingForm={showBiddingForm}
      />

      {showBiddingForm && (
        <ShortTermBiddingForm
          orderId={orderId}
          auction={auction.response.data as AuctionShortTerm}
          auctionConfig={auctionConfig.response.data}
          orders={orders?.response.data}
          setSelectedUndiscountedTariff={() => {
            // not needed currently as we don't show the starting price in the auction details card
          }}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      )}
    </div>
  );
};
